import {
  getFixedT,
  type PostProcessorModule,
  type ResourceKeys,
} from 'i18next';

import { clientConfig } from './clientConfig.ts';

interface I18nAliasKeyDef {
  key: ResourceKeys['translation'];
  alias: ResourceKeys['translation'];
}

interface I18nAliasType {
  aliases: I18nAliasKeyDef[];
  condition: boolean;
}

const stockAlias: I18nAliasType = {
  condition: clientConfig.locale === 'en-US',
  aliases: [
    {
      key: 'stock.stockTakes.createStockTake',
      alias: 'inventory.createInventoryCheck',
    },
    {
      key: 'stock.manageStockLevels',
      alias: 'inventory.manageInventoryLevels',
    },
    {
      key: 'stock.stockTakes.manageStockTakes',
      alias: 'inventory.manageInventoryChecks',
    },
    {
      key: 'stock.pageDescription',
      alias: 'inventory.pageDescription',
    },
    {
      key: 'stock.searchStock',
      alias: 'inventory.searchInventory',
    },
    {
      key: 'stock.stock',
      alias: 'inventory.inventory',
    },
    {
      key: 'stock.stockTakes.stockTake',
      alias: 'inventory.inventoryCheck',
    },
    {
      key: 'stock.stockTakes.stockTakes',
      alias: 'inventory.inventoryChecks',
    },
    {
      key: 'stock.stockLevels',
      alias: 'inventory.inventoryLevels',
    },
    {
      key: 'stock.stockOverview',
      alias: 'inventory.inventoryOverview',
    },
    {
      key: 'stockProfiles.stockProfiles',
      alias: 'inventory.inventoryProfiles',
    },
    {
      key: 'stock.stockMovements',
      alias: 'inventory.inventoryMovements',
    },
    {
      key: 'stockTakeProfiles.stockTakeProfile',
      alias: 'inventory.inventoryCheckProfile',
    },

    {
      key: 'stock.stockTakes.completeStockTake',
      alias: 'inventory.completeInventoryCheck',
    },
    {
      key: 'stock.stockTakes.addStockTakeItem',
      alias: 'inventory.addInventoryCheckItem',
    },
  ],
};

const allAliases: I18nAliasType[] = [stockAlias];

export const allAliasKeys = allAliases.flatMap(({ aliases }) => aliases);

const aliasResolver = (key: string, value: string, lng = 'en') => {
  const fixedT = getFixedT(lng, 'translation');

  const matchingAlias = allAliases
    .filter(({ condition }) => condition)
    .flatMap(({ aliases }) => aliases)
    .find((alias) => alias.key === key);

  return matchingAlias ? fixedT(matchingAlias.alias) : value;
};

export const i18nAlias: PostProcessorModule = {
  name: 'i18nAlias',
  type: 'postProcessor',
  process: (value, key, options) => {
    const adjustedKey = Array.isArray(key)
      ? key.length === 1
        ? key[0]
        : value
      : key;

    return adjustedKey === value
      ? value
      : aliasResolver(adjustedKey, value, options.lng);
  },
};
